// src/pages/HelpPage.tsx
import React, { useState, useCallback } from "react";
import { Searchbox } from "wl-ui-kit";
import { WL_RED } from "../utils/designTokens";

// --- Types ---
interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSection {
  title: string;
  items: FAQItem[];
}

interface GuideItem {
  title: string;
  steps: string[];
}

// --- FAQ Data ---
const faqSections: FAQSection[] = [
  {
    title: "Orders Management",
    items: [
      {
        question: "What is Orders Management?",
        answer:
          "Orders Management is the central workspace for viewing and monitoring all active shipments across your supply chain network. It provides real-time status updates, risk indicators, and filtering capabilities to help you track and manage order fulfillment.",
      },
      {
        question: "What do order statuses mean (Pending, In Transit, Ready, Delivered, Delayed)?",
        answer:
          "Order statuses indicate the current state of each shipment: Pending (order created but not yet shipped), In Transit (actively moving toward destination), Ready (arrived at destination facility awaiting pickup), Delivered (successfully completed), and Delayed (experiencing disruptions that impact expected delivery).",
      },
      {
        question: 'What does "High-Risk" indicate on an order?',
        answer:
          "High-Risk indicates that an order has a risk score of 60 or above, or is experiencing conditions that may lead to delays or compliance issues. This flag helps prioritize orders that require immediate attention or intervention.",
      },
      {
        question: "How is Risk Score calculated?",
        answer:
          "Risk Score is calculated based on multiple factors including weather conditions affecting the route, tariff changes, port congestion, carrier performance history, and transit time variance. The score ranges from 0-100, with higher scores indicating greater probability of disruption.",
      },
      {
        question: "What does Weather Risk mean?",
        answer:
          "Weather Risk indicates the level of potential disruption caused by weather events along the shipment route. Risk levels (Low, Medium, High) are determined by analyzing active weather alerts, historical impact data, and route exposure to affected regions.",
      },
      {
        question: "What is Tariff Impact?",
        answer:
          "Tariff Impact represents the total duty and tax costs associated with an order based on HTS classification, country of origin, and current tariff rates. This helps you understand the full landed cost of each shipment.",
      },
      {
        question: 'What does "Assigned Route" represent?',
        answer:
          "Assigned Route shows the planned transportation path for the shipment, including origin, destination, transit mode (Road, Air, Ship), and any intermediate waypoints. Route assignments are based on optimization parameters and carrier availability.",
      },
      {
        question: "How do filters work (Status, Risk Level, Search)?",
        answer:
          "Filters allow you to narrow the orders table to specific subsets. Status filters show orders in a particular state, Risk Level filters display orders by risk threshold, and Search enables text-based lookup by order ID, origin, destination, or carrier name.",
      },
      {
        question: 'How does "View Orders" from alerts work?',
        answer:
          'Clicking "View Orders" from an alert automatically navigates to the Orders page with pre-applied filters showing only the orders impacted by that specific alert. A banner at the top indicates the active filter and provides options to clear it or return to the Dashboard.',
      },
      {
        question: "Why is my order flagged as At Risk?",
        answer:
          "Orders are flagged as At Risk when they meet one or more criteria: risk score of 60 or above, delayed status, high or medium weather risk, or exposure to tariff changes. Click the order row to view detailed risk factors and recommended actions.",
      },
    ],
  },
  {
    title: "Compare (Scenario Builder & Route Options)",
    items: [
      {
        question: "What is the Compare workspace?",
        answer:
          "Compare is a scenario planning tool that allows you to evaluate multiple route options side-by-side based on cost, risk, and transit time. It helps you make data-driven decisions when selecting optimal transportation paths.",
      },
      {
        question: "What is a Scenario?",
        answer:
          "A Scenario is a configured set of parameters including origin, destination, optimization strategy, and risk tolerance. Scenarios can be saved and reused, allowing you to compare routing decisions across different time periods or constraint conditions.",
      },
      {
        question: "How does the Scenario Builder work?",
        answer:
          "The Scenario Builder guides you through defining route parameters: select origin and destination locations, choose an optimization strategy, set risk thresholds, and generate route options. The system then calculates and displays ranked alternatives based on your criteria.",
      },
      {
        question: "What does Optimization Strategy mean (Lowest Cost, Lowest Risk, Balanced, Fastest)?",
        answer:
          "Optimization Strategy determines how routes are prioritized: Lowest Cost minimizes total transportation and duty expenses, Lowest Risk selects routes with the most stable performance, Balanced weighs cost and risk equally, and Fastest prioritizes shortest transit time.",
      },
      {
        question: "What does Risk Threshold control?",
        answer:
          "Risk Threshold sets the maximum acceptable risk level for route options. Routes exceeding this threshold are flagged or excluded from recommendations. Adjusting the threshold helps balance risk tolerance with cost and speed requirements.",
      },
      {
        question: "How does the system generate route options?",
        answer:
          "The system evaluates available transportation lanes considering carrier capacity, transit mode combinations, historical performance data, current weather conditions, tariff rates, and port congestion. Algorithms rank options based on your selected optimization strategy.",
      },
      {
        question: "What data is used to evaluate routes?",
        answer:
          "Route evaluation uses historical lane performance, real-time weather intelligence, current tariff rates, carrier reliability metrics, port congestion data, and transit time variance. These inputs are combined to produce cost, risk, and time estimates for each option.",
      },
      {
        question: 'What does "Balanced" optimization mean?',
        answer:
          "Balanced optimization weighs cost, risk, and transit time equally when ranking route options. This strategy is recommended when no single factor takes priority and you want the most well-rounded solution.",
      },
      {
        question: "Can I save scenarios?",
        answer:
          "Yes. After building a scenario, click the Save Scenario button to store it for future reference. Saved scenarios retain all configuration parameters and can be accessed from the Saved Scenarios list.",
      },
      {
        question: "Where are Saved Scenarios stored?",
        answer:
          "Saved Scenarios are stored in your account and accessible from the Compare workspace. They appear in the Saved Scenarios panel, where you can load, edit, or delete them as needed.",
      },
      {
        question: "How are route costs estimated?",
        answer:
          "Route costs include freight charges based on carrier rate cards, tariff duties calculated from HTS codes and origin country, fuel surcharges, and applicable handling fees. Cost estimates are updated when tariff rates or carrier pricing changes.",
      },
      {
        question: "Does route comparison consider tariffs?",
        answer:
          "Yes. Tariff costs are factored into total landed cost calculations for each route option. The system applies current duty rates based on HTS classification and country of origin to provide comprehensive cost visibility.",
      },
      {
        question: "Does it factor weather disruptions?",
        answer:
          "Yes. Active weather alerts and historical disruption patterns are incorporated into risk scoring for each route. Routes passing through regions with active weather events receive higher risk ratings to reflect potential delays.",
      },
    ],
  },
  {
    title: "Simulate (Tariff & Cost Modeling)",
    items: [
      {
        question: "What is the Simulation Workspace?",
        answer:
          "The Simulation Workspace is a tariff and cost modeling tool that calculates duties, taxes, and total landed cost based on HTS code, country of origin, and shipment value. It enables what-if analysis by allowing you to adjust tariff rates and quantities.",
      },
      {
        question: "What is an HTS code?",
        answer:
          "HTS (Harmonized Tariff Schedule) code is a standardized numerical classification system used to identify products for customs and duty purposes. HTS codes determine the applicable tariff rate for imported goods.",
      },
      {
        question: "Where do I find the correct HTS code?",
        answer:
          "HTS codes can be found in official customs publications, tariff databases, or by consulting with customs brokers. The code must accurately describe the product to ensure correct duty calculation and compliance.",
      },
      {
        question: "What does Country of Origin impact?",
        answer:
          "Country of Origin determines which tariff rates, trade agreements, and duty exemptions apply to the shipment. Different origin countries may have significantly different duty rates for the same HTS code.",
      },
      {
        question: "How is Duty calculated?",
        answer:
          "Duty is calculated by multiplying the shipment value by the applicable tariff rate for the HTS code and country of origin combination. The tariff rate is sourced from official customs rate tables and updated when regulatory changes occur.",
      },
      {
        question: "What is Total Landed Cost?",
        answer:
          "Total Landed Cost is the sum of product cost, freight charges, insurance, duties, taxes, and handling fees. It represents the complete cost to deliver goods to the final destination and is essential for accurate pricing and profitability analysis.",
      },
      {
        question: "What does Per Unit Cost represent?",
        answer:
          "Per Unit Cost divides the Total Landed Cost by the quantity of units in the shipment. This metric helps compare cost efficiency across different shipment sizes and sourcing scenarios.",
      },
      {
        question: "How does Adjust Tariff Rate (slider) work?",
        answer:
          "The Adjust Tariff Rate slider allows you to model what-if scenarios by manually changing the tariff percentage. This is useful for evaluating the financial impact of proposed tariff changes or trade policy shifts before they take effect.",
      },
      {
        question: "What are What-If Scenarios?",
        answer:
          "What-If Scenarios enable you to test different assumptions (tariff rate changes, quantity adjustments, origin country switches) to see how they impact total landed cost. This supports proactive decision-making and contingency planning.",
      },
      {
        question: "Does simulation update automatically?",
        answer:
          "Yes. When you adjust inputs such as tariff rate, quantity, or unit value, the simulation recalculates duty, total landed cost, and per unit cost in real-time. This provides immediate feedback for scenario analysis.",
      },
      {
        question: "Is this calculation compliance-certified?",
        answer:
          "The calculation uses official tariff rate sources, but results should be verified with customs authorities before final submission. This tool is designed for operational planning and decision support, not as a substitute for formal compliance review.",
      },
      {
        question: "Does it include VAT, GST, or other taxes?",
        answer:
          "The simulation focuses on tariff duties. Additional taxes such as VAT, GST, or excise duties may apply depending on destination country regulations. Consult with customs brokers for comprehensive tax assessment.",
      },
    ],
  },
  {
    title: "Monitoring & Alerts",
    items: [
      {
        question: "What types of alerts exist?",
        answer:
          "The system generates four types of alerts: Route Alerts (port congestion, carrier delays), Tariff Alerts (rate changes affecting active shipments), Weather Alerts (storms or conditions impacting lanes), and SLA Alerts (orders at risk of missing delivery commitments).",
      },
      {
        question: "What triggers a congestion alert?",
        answer:
          "Congestion alerts are triggered when port or terminal wait times exceed normal thresholds, vessel berthing is delayed, or drayage operations experience significant backlog. These alerts identify shipments affected by the congestion and estimated delay duration.",
      },
      {
        question: "What triggers a tariff change alert?",
        answer:
          "Tariff change alerts are issued when official regulatory updates modify duty rates for HTS codes associated with active or upcoming shipments. The alert includes the new rate, effective date, and list of impacted orders.",
      },
      {
        question: "What triggers weather alerts?",
        answer:
          "Weather alerts are triggered when severe weather events (storms, floods, hurricanes) are detected along active shipment routes. The system assesses risk based on weather severity, route exposure, and potential delay impact.",
      },
      {
        question: 'What does "See Why" show?',
        answer:
          'Clicking "See Why" next to the At Risk indicator in the map legend navigates to the Orders page with filters pre-applied to show only orders flagged as high-risk. This provides quick access to the full list of at-risk shipments.',
      },
      {
        question: "How often are alerts refreshed?",
        answer:
          "Alerts are refreshed dynamically based on data source update frequency. Weather alerts update near real-time, tariff alerts reflect official regulatory publication schedules, and route performance alerts update periodically as carrier and port data becomes available.",
      },
      {
        question: "Can alerts be customized?",
        answer:
          "Alert thresholds and notification preferences can be configured in the Settings page. Customization options allow you to define risk tolerances, select which alert types to receive, and set priority levels for different shipment categories.",
      },
    ],
  },
];

// --- Data & Sources ---
const dataSources = {
  title: "Data & Sources",
  sections: [
    {
      heading: "Data Sources",
      content: [
        "Tariff rates sourced from official customs and regulatory publications.",
        "HTS classification data based on harmonized tariff schedule references.",
        "Weather data from third-party weather intelligence providers.",
        "Route performance derived from historical lane performance and carrier signals.",
        "Order data sourced from internal system records and integrations.",
      ],
    },
    {
      heading: "Update Frequency",
      content: [
        "Tariff rates: Updated when regulatory changes are published.",
        "Weather signals: Refreshed near real-time.",
        "Route performance: Periodically recalculated.",
        "Alerts: System-triggered and refreshed dynamically.",
      ],
    },
    {
      heading: "Disclaimer",
      content: [
        "Information provided is for operational decision support. Final compliance verification should be performed prior to customs submission.",
      ],
    },
  ],
};

// --- Guides ---
const guides: GuideItem[] = [
  {
    title: "Build and Compare a Route Scenario",
    steps: [
      "Navigate to the Compare workspace from the sidebar.",
      'Click "New Scenario" to open the Scenario Builder.',
      "Select origin and destination locations from the dropdowns.",
      "Choose an optimization strategy: Lowest Cost, Lowest Risk, Balanced, or Fastest.",
      "Adjust the risk threshold slider if needed to set acceptable risk levels.",
      'Click "Generate Routes" to view route options ranked by your criteria.',
      "Review the route comparison table showing cost, risk, transit time, and carrier details.",
      'Select your preferred route and click "Save Scenario" to store it for future reference.',
    ],
  },
  {
    title: "Save and Review Scenarios",
    steps: [
      'After generating route options in the Compare workspace, click "Save Scenario".',
      "Enter a descriptive name for the scenario to help identify it later.",
      "Confirm the save action to store the scenario in your account.",
      'Access saved scenarios from the "Saved Scenarios" panel in the Compare workspace.',
      "Click on any saved scenario to load its configuration and view route options.",
      "Edit scenario parameters and regenerate routes if conditions have changed.",
      "Delete outdated scenarios using the delete icon next to each saved item.",
    ],
  },
  {
    title: "Calculate Tariff Costs Using Simulate",
    steps: [
      "Navigate to the Simulate workspace from the sidebar.",
      "Enter the HTS code for the product you are importing.",
      "Select the country of origin from the dropdown menu.",
      "Input the shipment value (FOB or CIF basis) and quantity.",
      "Review the calculated duty, total landed cost, and per unit cost.",
      'Use the "Adjust Tariff Rate" slider to model what-if scenarios with different duty rates.',
      "Compare results to assess the impact of tariff changes on profitability.",
      "Export or document the simulation results for planning and budgeting purposes.",
    ],
  },
  {
    title: "Identify and Filter High-Risk Orders",
    steps: [
      "Navigate to the Orders page from the sidebar.",
      'Use the Risk Level filter dropdown to select "High" risk orders.',
      "Review the filtered list showing orders with risk scores of 60 or above.",
      "Click on any order row to view detailed risk factors and contributing conditions.",
      "Sort the table by Risk Score (descending) to prioritize the highest-risk shipments.",
      "Take action by contacting carriers, rerouting shipments, or escalating to management.",
      "Clear the filter to return to the full orders list when review is complete.",
    ],
  },
  {
    title: "Investigate Alerts and Impacted Orders",
    steps: [
      "Review active alerts displayed on the Dashboard or in the Alerts panel.",
      "Click on any alert card to open the detailed alert drawer.",
      "Read the alert description, impact summary, affected region, and financial impact.",
      "Note the number of impacted orders and affected routes listed in the drawer.",
      'Click "View Orders" to navigate to the Orders page with pre-applied filters.',
      "Review the filtered list showing only orders affected by the specific alert.",
      'Click "Back to Dashboard" to return to the main view or "Clear Filter" to see all orders.',
      "Take recommended actions such as planning optimal routes or contacting carriers.",
    ],
  },
];

// --- Component ---
const HelpPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0); // 0 = FAQs, 1 = Guides
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set([0])); // First section expanded by default
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleSection = useCallback((index: number) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, []);

  const toggleItem = useCallback((id: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Filter FAQ sections by search
  const filteredFAQSections = faqSections
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) =>
          item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.answer.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    }))
    .filter((section) => section.items.length > 0);

  // Filter guides by search
  const filteredGuides = guides.filter(
    (guide) =>
      guide.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      guide.steps.some((step) => step.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="bg-neutral-100 py-8 pl-12 pr-8">

      {/* Page Title */}
      <div className="mb-8 max-w-[900px]">
        <h1 className="text-lg font-bold text-wl-black mb-2 leading-tight">
          Help Center
        </h1>
        <p className="text-xs font-normal text-slate-500 leading-snug">
          Product guidance, data transparency, and operational support
        </p>
      </div>

      {/* Search Bar — wl-ui-kit Searchbox */}
      <div className="mb-8" style={{ border: 'none' }}>
        <Searchbox
          variant="primary"
          placeholder="Search help articles..."
          value={searchQuery}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
          className="w-full"
          style={{ border: 'none' }}
        />
      </div>

      {/* Custom Tabs — wl-ui-kit style */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-8" style={{ border: 'none' }}>
        <div className="flex border-b border-gray-200">
          <button
            type="button"
            onClick={() => setActiveTab(0)}
            className={`flex-1 px-6 py-3 text-[11px] font-semibold transition-all ${
              activeTab === 0
                ? "text-wl-red bg-red-50 border-b-2 border-wl-red"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
            style={{ 
              border: 'none',
              borderBottom: activeTab === 0 ? `2px solid #EE2D3D` : 'none',
              outline: 'none'
            }}
          >
            FAQs
          </button>
          <button
            type="button"
            onClick={() => setActiveTab(1)}
            className={`flex-1 px-6 py-3 text-[11px] font-semibold transition-all ${
              activeTab === 1
                ? "text-wl-red bg-red-50 border-b-2 border-wl-red"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
            style={{ 
              border: 'none',
              borderBottom: activeTab === 1 ? `2px solid #EE2D3D` : 'none',
              outline: 'none'
            }}
          >
            Guides
          </button>
        </div>
      </div>

      {/* FAQs Tab Content */}
      {activeTab === 0 && (
        <div className="flex flex-col gap-4">
          {filteredFAQSections.length > 0 ? (
            filteredFAQSections.map((section, sectionIndex) => (
              <div
                key={section.title}
                className="bg-gray-50 rounded-lg overflow-hidden border-0"
                style={{ border: 'none' }}
              >
                {/* Section header */}
                <button
                  type="button"
                  onClick={() => toggleSection(sectionIndex)}
                  className="w-full flex items-center justify-between px-8 min-h-[64px] bg-gray-50 hover:bg-gray-100 transition-colors text-left border-0"
                  style={{ border: 'none', outline: 'none' }}
                >
                  <span className="text-sm font-semibold text-gray-900">
                    {section.title}
                  </span>
                  <svg
                    className="w-5 h-5 text-gray-500 shrink-0 transition-transform"
                    style={{ transform: expandedSections.has(sectionIndex) ? "rotate(180deg)" : "rotate(0deg)" }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* FAQ items */}
                {expandedSections.has(sectionIndex) && (
                  <div>
                    {section.items.map((item, itemIndex) => {
                      const itemId = `${sectionIndex}-${itemIndex}`;
                      const isExpanded = expandedItems.has(itemId);
                      return (
                        <div key={item.question} className="mt-0.5 border-0" style={{ border: 'none' }}>
                          <button
                            type="button"
                            onClick={() => toggleItem(itemId)}
                            className="w-full flex items-center justify-between px-8 min-h-[64px] bg-white hover:bg-gray-100 transition-colors text-left border-0"
                            style={{ border: 'none', outline: 'none' }}
                          >
                            <span className="text-xs font-medium text-gray-800 pr-4">
                              {item.question}
                            </span>
                            <svg
                              className="w-[18px] h-[18px] text-gray-400 shrink-0 transition-transform"
                              style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)" }}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          {isExpanded && (
                            <div className="px-8 pt-4 pb-6 bg-white">
                              <p className="text-xs font-normal text-gray-500 leading-relaxed">
                                {item.answer}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500 text-center py-12">
              No results found for &quot;{searchQuery}&quot;
            </p>
          )}

          {/* Data Sources card */}
          {searchQuery === "" && (
            <div className="bg-gray-50 rounded-lg overflow-hidden border-0" style={{ border: 'none' }}>
              <div className="px-8 min-h-[64px] flex items-center bg-gray-50">
                <span className="text-xs font-semibold text-gray-900">
                  {dataSources.title}
                </span>
              </div>
              <div className="p-8">
                {dataSources.sections.map((sec) => (
                  <div key={sec.heading} className={sec.heading === dataSources.sections.at(-1)?.heading ? "" : "mb-7"}>
                    <p className="text-[11px] font-semibold text-gray-900 mb-4">
                      {sec.heading}
                    </p>
                    <ul className="list-none m-0 p-0 pl-1.5">
                      {sec.content.map((c, i) => (
                        <li key={c} className={`flex items-start ${i < sec.content.length - 1 ? "mb-4" : ""}`}>
                          <span
                            className={`mr-2 mt-0.5 text-sm shrink-0 ${
                              sec.heading === "Disclaimer" ? "text-gray-400" : "text-gray-700"
                            }`}
                          >
                            •
                          </span>
                          <p
                            className={`text-[10px] font-normal leading-relaxed flex-1 ${
                              sec.heading === "Disclaimer"
                                ? "text-gray-500 italic"
                                : "text-gray-700"
                            }`}
                          >
                            {c}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Guides Tab Content */}
      {activeTab === 1 && (
        <div className="flex flex-col gap-4">
          {filteredGuides.length > 0 ? (
            filteredGuides.map((guide, guideIndex) => {
              const guideId = `guide-${guideIndex}`;
              const isExpanded = expandedItems.has(guideId);
              return (
                <div
                  key={guide.title}
                  className="bg-gray-50 rounded-lg overflow-hidden border-0"
                  style={{ border: 'none' }}
                >
                  <button
                    type="button"
                    onClick={() => toggleItem(guideId)}
                    className="w-full flex items-center justify-between px-6 min-h-[52px] bg-gray-50 hover:bg-gray-100 transition-colors text-left border-0"
                    style={{ border: 'none', outline: 'none' }}
                  >
                    <span className="text-sm font-semibold text-gray-700">
                      {guide.title}
                    </span>
                    <svg
                      className="w-[18px] h-[18px] text-gray-400 shrink-0 transition-transform"
                      style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)" }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {isExpanded && (
                    <div className="p-6 bg-white">
                      {guide.steps.map((step, stepIndex) => (
                        <div
                          key={step}
                          className={`flex items-start gap-3 ${stepIndex < guide.steps.length - 1 ? "mb-4" : ""}`}
                        >
                          <div
                            className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-semibold text-white"
                            style={{ backgroundColor: WL_RED }}
                          >
                            {stepIndex + 1}
                          </div>
                          <p className="text-xs text-gray-500 leading-relaxed">
                            {step}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <p className="text-sm text-gray-500 text-center py-12">
              No results found for &quot;{searchQuery}&quot;
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default HelpPage;
